import { useEffect, useMemo, useState } from "react";
import {
  useJson, nf, fmtDate, control, Chip, ConfidenceBadge, IdBadge, entityPath, victimPath, listingPath, countryPath, go, CATEGORY_BY_SLUG, SANCTION_BODY, Box, OnThisPage,
} from "./shared";
import { BarList, ChoroplethMap, CountryLocator } from "./Charts";
import { ReportCard } from "./Reports";
import { StixBar } from "./Stix";
import { CountryCyberOps } from "./CyberOps";
import { CountryCensorship } from "./Censorship";

// Geopolitics › Countries: #/kb/countries (a map and a table of every country the site has
// something on; ?map=<metric> picks what the map shows) and a page per country,
// #/kb/country/<ISO2>, gathered from the other sections (knowledge.js, countryProfile).

const METRICS = [
  { key: "target", label: "Named as a target", unit: "groups", what: "ATT&CK groups and campaigns whose descriptions name it as a target" },
  { key: "origin", label: "Groups tied to it", unit: "groups", what: "ATT&CK groups and campaigns whose descriptions tie them to it" },
  { key: "opsFrom", label: "State cyber operations by it", unit: "incidents", what: "Incidents EuRepoC attributes to its state or groups affiliated with it, 2000–2024" },
  { key: "opsAgainst", label: "Cyber operations against it", unit: "incidents", what: "Incidents EuRepoC records against targets there, 2000–2024" },
  { key: "censored", label: "Websites blocked", unit: "tests", what: "Website tests that hit a block page there in the last 30 days (OONI)" },
  { key: "claims365", label: "Ransomware claims, 12 months", unit: "claims", what: "Victims ransomware groups claimed there in the last 12 months" },
  { key: "sanctionsOn", label: "Sanctions over it", unit: "listings", what: "Listings under the US, UN, EU, UK, Australian and Canadian sanctions over it" },
  { key: "sanctionsHere", label: "Sanctioned there", unit: "listings", what: "Sanctions listings with an address or nationality there" },
  { key: "exportControls", label: "US export controls", unit: "listings", what: "Listings there on the US export-control lists" },
  { key: "reports", label: "Reports naming it", unit: "reports", what: "Current reports that name it" },
];
const METRIC = Object.fromEntries(METRICS.map(m => [m.key, m]));
const COLUMNS = [
  ["name", "Country"], ["origin", "Groups tied to it"], ["target", "Named as target by"], ["opsFrom", "State cyber ops by it"], ["opsAgainst", "Cyber ops against it"], ["censored", "Blocked tests, 30 days"], ["claims365", "Ransomware claims, 12 mo"],
  ["sanctionsOn", "Sanctions over it"], ["sanctionsHere", "Sanctioned there"], ["exportControls", "Export controls"], ["reports", "Reports"], ["missiles", "Missile tests"],
];
const BODY_ORDER = ["us", "un", "eu", "uk", "au", "ca"];

const more = (ui, href, label) => <a href={href} style={{ fontSize: ui.fs(12), color: ui.B.accent, textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>{label} →</a>;
const note = (ui, text) => <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>{text}</span>;
const grid = (min) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`, gap: 14, alignItems: "start" });
const big = (ui, n, rest) => (
  <div style={{ fontSize: ui.fs(30), fontWeight: 900, color: ui.B.text, lineHeight: 1.1, marginBottom: 8 }}>
    {nf(n)}{rest && <span style={{ fontSize: ui.fs(13), fontWeight: 600, color: ui.B.textDim }}> {rest}</span>}
  </div>
);
const dim = (ui, text) => <div style={{ fontSize: ui.fs(12), color: ui.B.textDim, lineHeight: 1.5 }}>{text}</div>;

// The map's metric in the address, so a link opens the same map.
function keepMetric(key) {
  const [route, query = ""] = window.location.hash.split("?");
  const q = new URLSearchParams(query);
  if (key && key !== METRICS[0].key) q.set("map", key); else q.delete("map");
  const next = `${route}${q.toString() ? `?${q}` : ""}`;
  if (next !== window.location.hash) window.history.replaceState(null, "", next);
}

// ─── #/kb/countries ───

export function KbCountries({ ui, params = {} }) {
  const { B, fs, HP } = ui;
  const { data, error, loading } = useJson("/api/kb/countries");
  const [metric, setMetric] = useState(METRIC[params.map] ? params.map : METRICS[0].key);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: METRICS[0].key, desc: true });
  useEffect(() => { keepMetric(metric); setSort({ key: metric, desc: true }); }, [metric]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = (data?.countries || []).filter(c => !needle || c.name.toLowerCase().includes(needle) || c.iso2.toLowerCase() === needle);
    const dir = sort.desc ? -1 : 1;
    return [...list].sort((a, b) => (sort.key === "name" ? a.name.localeCompare(b.name) * dir : (a[sort.key] - b[sort.key]) * dir || a.name.localeCompare(b.name)));
  }, [data, q, sort]);

  const cat = CATEGORY_BY_SLUG.countries;
  const heading = (
    <>
      <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: "4px 0 6px", color: B.text }}>{cat.label}</h2>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 16px", maxWidth: "110ch", lineHeight: 1.6 }}>{cat.blurb} Pick a country on the map or in the table.</p>
    </>
  );
  if (loading) return <>{heading}<div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div></>;
  if (error) return <>{heading}<div style={{ color: "#dc2626", fontSize: fs(12) }}>{error}</div></>;

  const m = METRIC[metric];
  const values = Object.fromEntries(data.countries.filter(c => c[metric] > 0).map(c => [c.iso2, c[metric]]));
  const numeric = Object.fromEntries(data.countries.filter(c => c.numeric).map(c => [c.iso2, c.numeric]));
  const names = Object.fromEntries(data.countries.map(c => [c.iso2, c.name]));
  const waiting = [!data.loaded.sanctions && "sanctions lists", !data.loaded.exportControls && "export-control lists", !data.loaded.ransomware && "ransomware claims", !data.loaded.reports && "reports"].filter(Boolean);
  const pill = (on) => ({ ...HP.navBtn, padding: "5px 11px", fontSize: fs(12), ...(on ? { color: B.text, borderColor: B.primary, background: B.primary + "14", fontWeight: 700 } : {}) });
  const th = (key, label) => (
    <th key={key} style={{ padding: "9px 10px", fontWeight: 700, textAlign: key === "name" ? "left" : "right", whiteSpace: "nowrap" }}>
      <button onClick={() => setSort(s => ({ key, desc: s.key === key ? !s.desc : key !== "name" }))} style={{ background: "none", border: "none", padding: 0, font: "inherit", color: sort.key === key ? B.text : "inherit", cursor: "pointer", textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}{sort.key === key ? (sort.desc ? " ↓" : " ↑") : ""}
      </button>
    </th>
  );
  const cell = { padding: "8px 10px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), textAlign: "right", fontVariantNumeric: "tabular-nums", color: B.textMuted };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div>{heading}</div>
      {waiting.length > 0 && dim(ui, `Still loading: ${waiting.join(", ")}. They arrive a few minutes after the server starts; until then those counts are missing.`)}
      <Box title="Map" ui={ui} right={note(ui, m.what)}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {METRICS.map(x => <button key={x.key} onClick={() => setMetric(x.key)} style={pill(metric === x.key)}>{x.label}</button>)}
        </div>
        <ChoroplethMap key={metric} mode="count" values={values} numeric={numeric} names={names} unit={m.unit} ui={ui} onSelect={(iso2) => go(countryPath(iso2))} />
        {(metric === "origin" || metric === "target") && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: fs(11), color: B.textDim, marginTop: 10, flexWrap: "wrap" }}>
            Read from MITRE ATT&CK's descriptions by keyword rules, so a sentence can be misread; each country's page shows the sentences.
            <ConfidenceBadge confidence={data.confidence[metric]} ui={ui} />
          </div>
        )}
      </Box>
      <Box title={`${nf(data.countries.length)} countries`} ui={ui} right={<input value={q} onChange={e => setQ(e.target.value)} placeholder="Find a country" aria-label="Find a country" style={{ ...control(ui), width: 220, textTransform: "none", letterSpacing: 0 }} />}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead>
              <tr style={{ fontSize: fs(10), color: B.textDim, textTransform: "uppercase", letterSpacing: 1 }}>{COLUMNS.map(([k, l]) => th(k, l))}</tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.iso2}>
                  <td style={{ ...cell, textAlign: "left" }}><a href={countryPath(c.iso2)} style={{ color: B.text, fontWeight: 700, textDecoration: "none" }}>{c.name}</a></td>
                  {COLUMNS.slice(1).map(([k]) => <td key={k} style={{ ...cell, color: c[k] ? (k === sort.key ? B.text : B.textMuted) : B.textDim, fontWeight: k === sort.key && c[k] ? 700 : 400 }}>{c[k] ? nf(c[k]) : "–"}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && dim(ui, "No country by that name has anything here.")}
        </div>
      </Box>
      {dim(ui, "Groups: MITRE ATT&CK, read by keyword rules. Cyber operations: the European Repository of Cyber Incidents (EuRepoC), Global Dataset v1.3, CC BY-NC 4.0 (non-commercial). Censorship: OONI, the last 30 days, CC BY-NC-SA 4.0. Ransomware claims: ransomware.live (leak-site claims, not confirmed). Sanctions: OFAC, the UN Security Council, the EU, the UK, Australia and Canada. Export controls: the US Consolidated Screening List. Reports: the current reports from the Reports feeds, by keyword rules. Missile tests: CNS and GCAT, as on the missile map.")}
    </div>
  );
}

// ─── #/kb/country/<ISO2> ───

function ActorTable({ rows, ui, max = 12, empty }) {
  const { B, fs, getConf, TypeIcon } = ui;
  const [all, setAll] = useState(false);
  if (!rows.length) return dim(ui, empty);
  const shown = all ? rows : rows.slice(0, max);
  const cell = { padding: "8px 10px", borderTop: `1px solid ${B.border}`, fontSize: fs(12), verticalAlign: "top" };
  return (
    <>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            {shown.map(a => (
              <tr key={a.id}>
                <td style={{ ...cell, width: "34%", minWidth: 120 }}>
                  <a href={entityPath(a)} style={{ display: "inline-flex", gap: 6, alignItems: "center", color: B.text, fontWeight: 700, textDecoration: "none" }}>
                    <TypeIcon type={a.type} color={getConf(a.type).color} size={13} />{a.name}
                  </a>
                  <div style={{ marginTop: 3, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <IdBadge id={a.attackId} ui={ui} />
                    {a.ics && <a href="#/kb/ics" title="An attack on industrial control systems: see Critical Infrastructure › ICS / SCADA" style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf("attack-pattern").color}>Industrial systems</Chip></a>}
                  </div>
                </td>
                <td style={{ ...cell, color: B.textMuted, lineHeight: 1.55 }}>“{a.sentence}”</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > max && (
        <button onClick={() => setAll(v => !v)} style={{ ...control(ui), cursor: "pointer", marginTop: 10, padding: "6px 12px" }}>
          {all ? "Show fewer" : `Show all ${nf(rows.length)}`}
        </button>
      )}
    </>
  );
}

export function KbCountry({ cc, ui }) {
  const { B, fs, getConf } = ui;
  const { data, error, loading } = useJson(`/api/kb/country/${encodeURIComponent(cc)}`);
  const crumb = <div style={{ fontSize: fs(12), color: B.textDim }}><a href="#/kb/countries" style={{ color: B.textDim, textDecoration: "none" }}>Countries</a> / {data?.name || cc}</div>;
  if (loading) return <>{crumb}<div style={{ color: B.textDim, fontSize: fs(13), marginTop: 10 }}>Loading…</div></>;
  if (error) return <>{crumb}<div style={{ color: "#dc2626", fontSize: fs(12), marginTop: 10 }}>{error}</div></>;

  const { name, actors, ransomware: rw, sanctions: sanc, exportControls: ec, missiles, reports } = data;
  const sancOnTotal = sanc && !sanc.loading ? sanc.on.reduce((s, x) => s + x.total, 0) : 0;
  const facts = [
    actors.origin.length && `${nf(actors.origin.length)} ATT&CK group${actors.origin.length === 1 ? "" : "s"} or campaign${actors.origin.length === 1 ? "" : "s"} tied to it`,
    actors.target.length && `${nf(actors.target.length)} that name it as a target`,
    rw.total && `${nf(rw.last365)} ransomware claims in the last 12 months`,
    sancOnTotal && `${nf(sancOnTotal)} sanctions listings over it`,
    reports.total && `${nf(reports.total)} current report${reports.total === 1 ? "" : "s"} name it`,
  ].filter(Boolean);
  const reportLink = `#/kb/reports?mentions=${encodeURIComponent(`country:${data.iso2}`)}`;
  const kindLabel = { origin: "as the attackers' country", target: "as a target" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          {crumb}
          <h2 style={{ fontSize: fs(26), fontWeight: 900, margin: "6px 0 4px", color: B.text }}>{name}</h2>
          <div style={{ fontSize: fs(13), color: B.textMuted, lineHeight: 1.6 }}>{facts.length ? facts.join(" · ") : "Nothing on file for this country yet."}</div>
          <StixBar apiKey={`country:${data.iso2}`} ui={ui} />
          <OnThisPage ui={ui} />
        </div>
        {data.numeric && <CountryLocator numeric={data.numeric} ui={ui} />}
      </div>

      <div style={grid(300)}>
        <Box title={`Sanctions over ${name}`} jump="Sanctions over it" ui={ui} right={sancOnTotal ? more(ui, `#/kb/sanctions?about=${data.iso2}`, "See them") : null}>
          {!sanc ? dim(ui, "Not a country on the sanctions lists.")
            : sanc.loading ? dim(ui, "Loading… the sanctions lists arrive several minutes after the server starts.")
            : sanc.on.length ? <>
              {big(ui, sancOnTotal, "listings")}
              <BarList rows={BODY_ORDER.map(b => sanc.on.find(x => x.body === b)).filter(Boolean).map(x => ({ label: SANCTION_BODY[x.body], value: x.total, href: `#/kb/sanctions?about=${data.iso2}&body=${x.body}` }))} ui={ui} unit="Listings" keepOrder />
              {sanc.why && <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55, marginTop: 10 }}>{sanc.why}</div>}
            </> : dim(ui, "No US, UN, EU, UK, Australian or Canadian sanctions programme is about it.")}
        </Box>
        <Box title="Sanctioned listings there" ui={ui} right={sanc && !sanc.loading && sanc.here ? more(ui, `#/kb/sanctions?country=${data.iso2}`, "See them") : null}>
          {!sanc || sanc.loading ? dim(ui, sanc?.loading ? "Loading…" : "None.")
            : sanc.here ? <>
              {big(ui, sanc.here, "listed there")}
              {sanc.whose.length > 0 && <BarList rows={sanc.whose.map(w => ({ label: w.label, value: w.total, href: `#/kb/sanctions?country=${data.iso2}&about=${encodeURIComponent(w.about)}` }))} ui={ui} unit="Listings" maxRows={6} />}
              {dim(ui, "People, organisations, ships and aircraft with an address or nationality there, by what they're sanctioned over.")}
            </> : dim(ui, "No sanctions listing has an address or nationality there.")}
        </Box>
        <Box title="US export controls" ui={ui} right={ec.total ? more(ui, `#/kb/export-controls?country=${data.iso2}`, "See them") : null}>
          {ec.loading ? dim(ui, "Loading… the lists arrive several minutes after the server starts.")
            : ec.total ? <>
              {big(ui, ec.total, "listings there")}
              <BarList rows={ec.lists.map(l => ({ label: l.name, value: l.count, href: `#/kb/export-controls?country=${data.iso2}&list=${l.key}` }))} ui={ui} unit="Listings" maxRows={6} />
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 10 }}>
                {ec.items.slice(0, 5).map(r => (
                  <a key={r.id} href={listingPath(r.id)} style={{ fontSize: fs(12), color: B.text, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.name} <span style={{ color: B.textDim }}>· {r.listName}{r.start ? ` · ${fmtDate(r.start)}` : ""}</span>
                  </a>
                ))}
              </div>
            </> : dim(ui, "Nothing there on the US export-control lists.")}
        </Box>
      </div>

      <div style={grid(420)}>
        <Box title={`Groups tied to ${name}`} jump="Groups tied to it" ui={ui} right={<span style={{ display: "flex", gap: 8, alignItems: "center", textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>MITRE's words, read by keyword rules <ConfidenceBadge confidence={actors.confidence.origin} ui={ui} /></span>}>
          <ActorTable rows={actors.origin} ui={ui} empty="No ATT&CK group or campaign description ties a group to it." />
        </Box>
        <Box title={`Groups that target ${name}`} jump="Groups that target it" ui={ui} right={<span style={{ display: "flex", gap: 8, alignItems: "center", textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>MITRE's words, read by keyword rules <ConfidenceBadge confidence={actors.confidence.target} ui={ui} /></span>}>
          <ActorTable rows={actors.target} ui={ui} empty="No ATT&CK group or campaign description names it as a target." />
        </Box>
      </div>

      <CountryCyberOps ops={data.cyberOps} name={name} iso2={data.iso2} ui={ui} />

      <CountryCensorship iso2={data.iso2} name={name} summary={data.censorship} ui={ui} />

      <div style={grid(300)}>
        <Box title="Ransomware claims, last 12 months" jump="Ransomware claims" ui={ui} right={rw.total ? more(ui, `#/kb/victims?country=${data.iso2}`, "All claims") : null}>
          {!rw.builtAt ? dim(ui, "Loading…")
            : rw.total ? <>
              {big(ui, rw.last365, `of ${nf(rw.total)} in all · ${nf(rw.last30)} in the last 30 days`)}
              {rw.groups.length ? <BarList rows={rw.groups.map(g => ({ label: g.label, value: g.count, href: g.link ? `#/kb/e/${encodeURIComponent(g.link)}` : undefined }))} ui={ui} unit="Claims" maxRows={8} /> : dim(ui, "None in the last 12 months.")}
            </> : dim(ui, "No ransomware group has claimed a victim there.")}
        </Box>
        {rw.sectors.length > 0 && (
          <Box title="Sectors hit, last 12 months" listed={false} ui={ui}>
            <BarList rows={rw.sectors.map(s => ({ label: s.sector, value: s.count }))} ui={ui} unit="Claims" maxRows={8} />
          </Box>
        )}
        {rw.recent.length > 0 && (
          <Box title="Latest claims" listed={false} ui={ui} right={note(ui, "leak-site claims, not confirmed")}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {rw.recent.map(r => (
                <div key={r.vid} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: fs(12), minWidth: 0 }}>
                  <a href={victimPath(r.vid)} style={{ color: B.text, fontWeight: 600, textDecoration: "none", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</a>
                  <span style={{ color: getConf("intrusion-set").color, whiteSpace: "nowrap" }}>{r.group.label}</span>
                  <span style={{ color: B.textDim, whiteSpace: "nowrap" }}>{r.day ? fmtDate(r.day) : ""}</span>
                </div>
              ))}
            </div>
          </Box>
        )}
        {missiles && (
          <Box title="Missile tests" ui={ui} right={more(ui, `#/kb/missiles?country=${data.iso2.toLowerCase()}`, "On the map")}>
            {big(ui, missiles.count, "launches")}
            {dim(ui, `${missiles.first ? fmtDate(missiles.first) : "?"} to ${missiles.last ? fmtDate(missiles.last) : "?"}${missiles.combat ? `, ${nf(missiles.combat)} of them in combat` : ""}. From ${missiles.source}.`)}
          </Box>
        )}
      </div>

      <Box title={`Reports that name ${name}`} jump="Reports" ui={ui} right={reports.total > reports.items.length ? more(ui, reportLink, `All ${nf(reports.total)}`) : note(ui, "found by keyword rules")}>
        {reports.items.length ? <>
          <div style={grid(300)}>
            {reports.items.map(r => <ReportCard key={r.id} s={r} ui={ui} extra={<Chip ui={ui}>{kindLabel[r.countryKind]}</Chip>} />)}
          </div>
          {dim(ui, "A country is read as the attackers' when the words around it say so (“Russian state-sponsored”, “attributed to China”), otherwise as a target or a place the report mentions. Open a report to see how it's named.")}
        </> : dim(ui, "None of the current reports names it.")}
      </Box>
    </div>
  );
}
